// HomePage.jsx
import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./HomePage.css";
import StatisticsModal from "./StatisticsModal";

const MASONIC_LINK_GROUPS = [
    {
        title: "Irish Constitution",
        links: [
            { label: "Grand Lodge of Ireland", url: "https://freemason.ie/", note: "Official Grand Lodge site." },
            { label: "Irish Freemasonry in South Africa", url: "https://irishfreemasonry.co.za/", note: "South African Irish Constitution news and galleries." },
            { label: "Irish Provincial Grand Lodges", url: "https://freemason.ie/about-grand-lodge/provincial-grand-lodges/", note: "Irish and overseas provinces." },
            { label: "Irish Freemasonry News & Events", url: "https://irishfreemasonry.co.za/news-and-events/", note: "Provincial news and charity updates." },
        ],
    },
    {
        title: "South Africa",
        links: [
            { label: "Grand Lodge of South Africa", url: "https://grandlodge.org.za/web/index.asp", note: "Official Grand Lodge of South Africa site." },
            { label: "Freemasonry in Southern Africa", url: "https://freemasonrysa.org/contact-us/", note: "Southern African English Freemasonry contacts." },
            { label: "District Grand Lodge of South Africa North", url: "https://www.dglsanorth.org.za/", note: "English Freemasonry in South Africa North." },
            { label: "District Grand Lodge of South Africa Eastern Division", url: "https://freemasons.org.za/", note: "English Freemasonry in the Eastern Cape." },
        ],
    },
    {
        title: "Wider Freemasonry",
        links: [
            { label: "United Grand Lodge of England", url: "https://www.ugle.org.uk/", note: "Official UGLE site." },
            { label: "Grand Lodge of Scotland", url: "https://www.grandlodgescotland.com/", note: "Official home of Scottish Freemasonry." },
        ],
    },
];

function CRLPrivacyModal({ open, onClose }) {
    const dialogRef = useRef(null);

    useEffect(() => {
        if (open) {
            dialogRef.current?.focus();
            document.body.classList.add("crlpm-modal-open");
            return () => document.body.classList.remove("crlpm-modal-open");
        }
    }, [open]);

    if (!open) return null;

    return (
        <div
            className="crlpm-overlay"
            role="presentation"
            onClick={onClose}
        >
            <div
                className="crlpm-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="crlpm-title"
                aria-describedby="crlpm-desc"
                onClick={(e) => e.stopPropagation()}
                tabIndex={-1}
                ref={dialogRef}
            >
                <div className="crlpm-header">
                    <h2 id="crlpm-title">Privacy Notice</h2>
                    <button
                        className="crlpm-close"
                        onClick={onClose}
                        aria-label="Close privacy notice"
                    >
                        ×
                    </button>
                </div>

                <div className="crlpm-body" id="crlpm-desc">
                    <p>
                        <strong>Why we keep records:</strong> Connaught Research Lodge preserves records to protect our history and support Masonic research. These records form part of our heritage for current and future generations.
                    </p>

                    <p>
                        <strong>What we keep:</strong> Member and lodge information such as names, roles, contact details, membership history, and documents linked to lodge activities and research.
                    </p>

                    <p>
                        <strong>Who can see it:</strong> Access is limited to authorised members of the Irish Constitution (in South Africa and abroad) and approved researchers where relevant. We don’t sell information and don’t share it outside the lodge without permission or lawful authority.
                    </p>

                    <p>
                        <strong>How we protect it:</strong> Access is password-controlled and activity is logged. Please keep your login details private and use strong passwords.
                    </p>

                    <p>
                        <strong>How long we keep it:</strong> Our aim is to preserve records permanently as part of the lodge’s cultural and Masonic heritage. Records are safeguarded so they can be used for historical, educational, and research purposes.
                    </p>

                    <p>
                        <strong>Your choices:</strong> You can request access to your information, ask for corrections, or (where the law allows) request removal of certain details. Please contact the Connaught Research Lodge Archivist or Secretary.
                    </p>

                    <p>
                        <strong>Our responsibility:</strong> We manage records in line with applicable privacy laws, including South Africa’s POPIA, and follow our lodge’s governance rules and traditions.
                    </p>
                </div>

                <div className="crlpm-actions">
                    <button className="crlpm-btn" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
}

function MasonicLinksModal({ open, onClose }) {
    const dialogRef = useRef(null);

    useEffect(() => {
        if (open) {
            dialogRef.current?.focus();
            document.body.classList.add("crlpm-modal-open");
            return () => document.body.classList.remove("crlpm-modal-open");
        }
    }, [open]);

    if (!open) return null;

    return (
        <div className="home-links-overlay" role="presentation" onClick={onClose}>
            <div
                className="home-links-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="home-links-title"
                onClick={(e) => e.stopPropagation()}
                tabIndex={-1}
                ref={dialogRef}
            >
                <div className="home-links-header">
                    <div>
                        <p>Masonic references</p>
                        <h2 id="home-links-title">Useful Links</h2>
                    </div>
                    <button type="button" onClick={onClose} aria-label="Close links">x</button>
                </div>

                <div className="home-links-groups">
                    {MASONIC_LINK_GROUPS.map((group) => (
                        <section key={group.title}>
                            <h3>{group.title}</h3>
                            <div>
                                {group.links.map((link) => (
                                    <a key={link.url} href={link.url} target="_blank" rel="noreferrer">
                                        <strong>{link.label}</strong>
                                        <span>{link.note}</span>
                                    </a>
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            </div>
        </div>
    );
}

function HomePage({ user, openLoginModal }) {
    const navigate = useNavigate();
    const [stagingCount, setStagingCount] = useState(0);
    const [hoveredSection, setHoveredSection] = useState(null);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const [showPrivacy, setShowPrivacy] = useState(false);
    const [showStatistics, setShowStatistics] = useState(false);
    const [showMasonicLinks, setShowMasonicLinks] = useState(false);

    // Pending users (role_id = 1)
    const [pendingUsers, setPendingUsers] = useState([]);
    const [pendingOpen, setPendingOpen] = useState(false);

    // Storage info (lodge storage vs OS vs free)
    const [storageInfo, setStorageInfo] = useState(null);
    const [storageError, setStorageError] = useState(null);
    const [archiveStats, setArchiveStats] = useState(null);

    const sections = [
        {
            name: "Attendance & RSVPs",
            icon: "/images/surveys.svg",
            route: "/surveys",
            allowedRoles: [],
            public: true
        },
        {
            name: "Public & Research Hub",
            icon: "/images/research.png",
            route: "/research",
            allowedRoles: [],
            public: true
        },
        {
            name: "About",
            icon: "/images/about.png",
            route: "/about",
            allowedRoles: [],
            public: true
        },
        { name: "Resources",      icon: "/images/resources.png",    route: "/resources",       allowedRoles: ["Admin", "Archivist", "Member", "Researcher", "Secretary"] },
        {
            name: "Statistics",
            icon: "/images/stats.png",
            action: () => setShowStatistics(true),
            allowedRoles: ["Admin", "Archivist", "Member", "Researcher", "Secretary"]
        },
        { name: "Calendar",       icon: "/images/calendar.png",     route: "/calendar",        allowedRoles: ["Admin", "Archivist", "Member", "Researcher", "Secretary"] },
        { name: "View Documents", icon: "/images/view.png",         route: "/view-documents",  allowedRoles: ["Admin", "Archivist", "Member", "Researcher", "Secretary"] },
        { name: "Upload Documents", icon: "/images/upload.png",     route: "/upload-documents",allowedRoles: ["Admin", "Archivist", "Secretary"] },
        {
            name: "Staging Imports",
            icon: "/images/upload.png",
            route: "/staging-imports",
            allowedRoles: ["Admin", "Archivist"]
        },
        { name: "Admin",          icon: "/images/history.png",      route: "/admin",           allowedRoles: ["Admin", "Archivist"] },
        { name: "Instructions",   icon: "/images/instructions.png", route: "/instructions",    allowedRoles: ["Admin", "Archivist", "Member", "Researcher", "Secretary"] },
        { name: "Analytics",      icon: "/images/analytics.png",    route: "/analytics",       allowedRoles: ["Admin", "Archivist"] },
    ];

    const handleMouseEnter = (e, section, hasAccess) => {
        if (!hasAccess) {
            setHoveredSection(section.name);
            const rect = e.currentTarget.getBoundingClientRect();
            setTooltipPosition({ top: rect.top - 40, left: rect.left + rect.width / 2 });
        }
    };

    const handleMouseLeave = () => setHoveredSection(null);

    const handleSectionClick = (section) => {
        if (section.public || section.allowedRoles.length === 0) {
            if (section.action) section.action();
            else navigate(section.route);
            return;
        }
        if (user && section.allowedRoles.includes(user.role)) {
            if (section.action) section.action();
            else navigate(section.route);
        } else {
            openLoginModal();
        }
    };

    useEffect(() => {
        let cancelled = false;

        axios.get("/api/public/site-stats")
            .then((res) => {
                if (!cancelled) setArchiveStats(res.data || null);
            })
            .catch((err) => {
                console.error("Error fetching public site stats:", err);
                if (!cancelled) setArchiveStats(null);
            });

        return () => {
            cancelled = true;
        };
    }, []);

    // Fetch users with role_id = 1 for Admin / Archivist
    useEffect(() => {
        if (!user) return;
        if (!["Admin", "Archivist"].includes(user.role)) return;

        const fetchPending = async () => {
            try {
                const res = await axios.get("/api/pending-users");
                setPendingUsers(res.data.users || []);
            } catch (err) {
                console.error("Error fetching pending users:", err);
            }
        };

        fetchPending();
    }, [user]);

    // Fetch storage breakdown for Admin / Archivist
    useEffect(() => {
        if (!user) return;
        if (!["Admin", "Archivist"].includes(user.role)) return;

        const fetchStorage = async () => {
            try {
                const res = await axios.get("/api/storage-breakdown");
                const { documents, osUsed: rawOsUsed, free, size, used } = res.data || {};

                // documents can be:
                // - a number (bytes), OR
                // - an object keyed by extension: { ".pdf": { bytes, files }, ... }
                let documentsBytes = 0;
                if (typeof documents === "number" && Number.isFinite(documents)) {
                    documentsBytes = documents;
                } else if (documents && typeof documents === "object") {
                    documentsBytes = Object.values(documents).reduce((sum, entry) => {
                        if (
                            entry &&
                            typeof entry.bytes === "number" &&
                            Number.isFinite(entry.bytes)
                        ) {
                            return sum + entry.bytes;
                        }
                        return sum;
                    }, 0);
                }

                const freeBytes =
                    typeof free === "number" && Number.isFinite(free) ? free : 0;
                const usedBytes =
                    typeof used === "number" && Number.isFinite(used) ? used : 0;

                let osUsedBytes;
                if (
                    typeof rawOsUsed === "number" &&
                    Number.isFinite(rawOsUsed)
                ) {
                    osUsedBytes = rawOsUsed;
                } else if (usedBytes > 0) {
                    osUsedBytes = Math.max(usedBytes - documentsBytes, 0);
                } else {
                    osUsedBytes = 0;
                }

                // size may be provided; otherwise infer from components
                let sizeBytes =
                    typeof size === "number" && Number.isFinite(size)
                        ? size
                        : documentsBytes + osUsedBytes + freeBytes;

                if (!sizeBytes || sizeBytes <= 0) {
                    setStorageInfo(null);
                    setStorageError("Storage size is zero or unavailable");
                    return;
                }

                const docPercent = (documentsBytes / sizeBytes) * 100;
                const osPercent = (osUsedBytes / sizeBytes) * 100;
                const freePercent = (freeBytes / sizeBytes) * 100;

                setStorageInfo({
                    documents: documentsBytes,
                    osUsed: osUsedBytes,
                    free: freeBytes,
                    size: sizeBytes,
                    used: usedBytes,
                    docPercent,
                    osPercent,
                    freePercent,
                });
                setStorageError(null);
            } catch (err) {
                console.error("Error fetching storage info:", err);
                setStorageError("Unable to fetch storage info");
                setStorageInfo(null);
            }
        };

        fetchStorage();
    }, [user]);

    useEffect(() => {
        if (!user) return;
        if (!["Admin", "Archivist"].includes(user.role)) return;

        const fetchStagingCount = async () => {
            try {
                const res = await axios.get("/api/email-imports/count");
                setStagingCount(res.data.count || 0);
            } catch (err) {
                console.error("Error fetching staging count:", err);
            }
        };

        fetchStagingCount();
    }, [user]);

    const hasAdminView = user && ["Admin", "Archivist"].includes(user.role);
    const pendingCount = pendingUsers.length;

    // Safely convert bytes → GB
    const toGB = (bytes) => {
        if (typeof bytes !== "number" || !Number.isFinite(bytes) || bytes < 0) {
            return "0.00";
        }
        return (bytes / (1024 ** 3)).toFixed(2);
    };

    const formatStatNumber = (value) => {
        const number = Number(value);
        return Number.isFinite(number) ? number.toLocaleString("en-ZA") : "--";
    };

    const archiveInventory = archiveStats?.inventory || {};
    const archiveKpis = archiveStats?.kpis || {};
    const protectedInventoryTotal = archiveStats
        ? Number(archiveInventory.documents || 0) +
          Number(archiveInventory.meetings || 0) +
          Number(archiveInventory.degree_records || 0) +
          Number(archiveInventory.members || 0)
        : undefined;

    const homeArchiveSnapshot = [
        { label: "registered users", value: archiveStats?.registeredUsers?.total },
        { label: "site-wide unique IPs", value: archiveKpis.unique_visitors },
        { label: "documents", value: archiveInventory.documents },
        { label: "inventory items", value: protectedInventoryTotal },
    ];

    return (
        <div className="home-page">
            <section className="home-archive-hero">
                <div className="introduction">
                    <p className="home-eyebrow">Connaught Research Lodge No. 361 I.C.</p>
                    <h1>Preserving the lodge archive for future research.</h1>
                    <p>
                        Connaught Research Lodge, under the Provincial Grand Lodge of South Africa Northern
                        (Irish Constitution), focuses on exploring and preserving the history, traditions, and
                        philosophy of Freemasonry.
                    </p>
                    <p>
                        Most features are reserved for verified members and administrators to ensure a secure
                        environment for Masonic research. For Connaught Lodge or archive-access enquiries, contact{" "}
                        <a href="mailto:connaught361archive@gmail.com">connaught361archive@gmail.com</a>.
                    </p>
                    <p className="home-contact-note">
                        Please note: this is a Freemasonry research archive. General public enquiries and unrelated
                        requests cannot be entertained through this portal.
                    </p>
                    <p className="home-citation-note">
                        Please cite archive material as: Connaught Research Lodge Archive,
                        Connaught Research Lodge No. 361 I.C., https://connaught361.co.za,
                        accessed [date]. Include any source listed on the entry.
                    </p>
                </div>
                <aside className="home-archive-glance" aria-label="Archive at a glance">
                    <div className="home-archive-glance-heading">
                        <span>Archive at a glance</span>
                        <small>Live totals</small>
                    </div>
                    <div className="home-archive-glance-grid">
                        {homeArchiveSnapshot.map((stat) => (
                            <div className="home-archive-glance-item" key={stat.label}>
                                <strong>{formatStatNumber(stat.value)}</strong>
                                <span>{stat.label}</span>
                            </div>
                        ))}
                    </div>
                </aside>
            </section>

            {/* Admin / Archivist-only management strip */}
            {hasAdminView && (
                <>
                    {/* Pending users indicator */}
                    <div className="pending-users-container">
                        <button
                            className="pending-users-button"
                            onClick={() => setPendingOpen((open) => !open)}
                            aria-expanded={pendingOpen}
                            aria-haspopup="true"
                        >
                            <span className="pending-icon" aria-hidden="true" />
                            <span className="pending-text">
                                {pendingCount === 0
                                    ? "No users awaiting role update"
                                    : `${pendingCount} user${pendingCount > 1 ? "s" : ""} awaiting role update`}
                            </span>
                            {pendingCount > 0 && (
                                <span className="pending-badge">{pendingCount}</span>
                            )}
                        </button>

                        {pendingOpen && pendingCount > 0 && (
                            <div className="pending-dropdown">
                                <ul>
                                    {pendingUsers.map((u) => (
                                        <li key={u.id} className="pending-item">
                                            <div className="pending-name">
                                                {u.first_name} {u.last_name}
                                            </div>
                                            <div className="pending-meta">
                                                <span>{u.email}</span>
                                                <span>Username: {u.username}</span>
                                                <span>
                                                    Registered:{" "}
                                                    {new Date(u.created_at).toLocaleDateString("en-ZA")}
                                                </span>
                                                <span>
                                                    Current role: {u.role_name} (id {u.role_id})
                                                </span>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>

                    {/* Server storage info */}
                    <div className="storage-info-card">
                        <div className="storage-header">
                            <strong>Server Storage</strong>
                        </div>

                        {storageError && (
                            <div className="storage-error">
                                {storageError}
                            </div>
                        )}

                        {!storageError && !storageInfo && (
                            <div className="storage-loading">
                                Loading storage info…
                            </div>
                        )}

                        {storageInfo && !storageError && (
                            <div className="storage-body">
                                <div className="storage-bar-wrapper">

                                    <div
                                        className="storage-os"
                                        style={{
                                            width: `${Math.max(
                                                0,
                                                Math.min(storageInfo.osPercent, 100)
                                            ).toFixed(1)}%`,
                                        }}
                                        title="Reserved (OS / other usage)"
                                    />
                                    <div
                                        className="storage-documents"
                                        style={{
                                            width: `${Math.max(
                                                0,
                                                Math.min(storageInfo.docPercent, 100)
                                            ).toFixed(1)}%`,
                                        }}
                                        title="Lodge storage (archived documents, media, etc.)"
                                    />
                                    <div
                                        className="storage-free"
                                        style={{
                                            width: `${Math.max(
                                                0,
                                                Math.min(storageInfo.freePercent, 100)
                                            ).toFixed(1)}%`,
                                        }}
                                        title="Free space"
                                    />
                                </div>

                                <div className="storage-text">
                                    <div className="storage-legend-item">
                                        <span className="storage-swatch storage-swatch-os" />
                                        <span>
                                            Reserved (OS / Other): {toGB(storageInfo.osUsed)} GB
                                        </span>
                                    </div>
                                    <div className="storage-legend-item">
                                        <span className="storage-swatch storage-swatch-docs" />
                                        <span>
                                            Lodge storage: {toGB(storageInfo.documents)} GB
                                        </span>
                                    </div>
                                    <div className="storage-legend-item">
                                        <span className="storage-swatch storage-swatch-free" />
                                        <span>
                                            Free: {toGB(storageInfo.free)} GB
                                        </span>
                                    </div>
                                    <div className="storage-legend-item">
                                        <span>Total: {toGB(storageInfo.size)} GB</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Privacy info card — only shown when logged in */}
            {user && (
                <div
                    className="privacy-info-card"
                    role="button"
                    tabIndex={0}
                    onClick={() => setShowPrivacy(true)}
                    onKeyDown={(e) => e.key === "Enter" && setShowPrivacy(true)}
                    aria-label="Open privacy notice"
                >
                    <div className="privacy-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" width="24" height="24">
                            <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.15" />
                            <path d="M12 8a1 1 0 110-2 1 1 0 010 2zm-1 3h2v7h-2v-7z" fill="currentColor" />
                        </svg>
                    </div>
                    <div className="privacy-text">
                        <strong>Privacy Notice</strong>
                        <span>How we handle member data (POPIA).</span>
                    </div>
                </div>
            )}

            <section className="sections-grid">
                {sections.map((section) => {
                    const hasAccess =
                        section.public ||
                        section.allowedRoles.length === 0 ||
                        (user && section.allowedRoles.includes(user.role));
                    return (
                        <div
                            key={section.name}
                            className={`section-card ${hasAccess ? "" : "disabled-card"}`}
                            onClick={(e) => hasAccess && handleSectionClick(section)}
                            onMouseEnter={(e) => handleMouseEnter(e, section, hasAccess)}
                            onMouseLeave={handleMouseLeave}
                            role="button"
                            tabIndex={0}
                            aria-label={`Navigate to ${section.name}`}
                            onKeyDown={(e) =>
                                e.key === "Enter" && hasAccess && handleSectionClick(section)
                            }
                        >
                            <div className="section-icon-wrapper">
                                <img
                                    src={section.icon}
                                    alt={`${section.name} icon`}
                                    className="section-icon"
                                />

                                {section.route === "/staging-imports" && stagingCount > 0 && (
                                    <span className="section-badge">
                {stagingCount}
            </span>
                                )}
                            </div>

                            <h3 className="section-name">{section.name}</h3>
                        </div>
                    );
                })}
                {hoveredSection && (
                    <div
                        className="tooltip"
                        style={{ top: `${tooltipPosition.top}px`, left: `${tooltipPosition.left}px` }}
                    >
                        Unauthorized: Please log in to access this section.
                    </div>
                )}
            </section>

            <CRLPrivacyModal open={showPrivacy} onClose={() => setShowPrivacy(false)} />
            <MasonicLinksModal open={showMasonicLinks} onClose={() => setShowMasonicLinks(false)} />
            {showStatistics && <StatisticsModal onClose={() => setShowStatistics(false)} />}
        </div>
    );
}

export default HomePage;
